import type * as backend from './backend-types';
import { HealthdotDisplayStatus } from '../types';
import { log } from './log';

/**
 * HQS-2142: the projection of the nine HQS-2138 statuses onto the six the app displays.
 *
 * The mapping is total over HQS-2138. `NEVERRECEIVEDVITALS` and `NOVITALS` both mean the
 * device is reachable but its measurements are unusable; `INTERRUPTED` and
 * `MALFUNCTIONING` both mean it is unreachable. The two are kept apart because they lead
 * to different corrective action at the bedside.
 */
const DISPLAY_STATUS: Record<backend.HealthdotConnectionState, HealthdotDisplayStatus> = {
  CONNECTING: 'connecting',
  WAITINGFORFIRSTVITALS: 'waitingForFirstVitals',
  CONNECTED: 'connected',
  NEVERRECEIVEDVITALS: 'interruptedNoVitals',
  NOVITALS: 'interruptedNoVitals',
  INTERRUPTED: 'interruptedNoCommunication',
  MALFUNCTIONING: 'interruptedNoCommunication',
  FINISHED: 'statusUnavailable',
  UNKNOWN: 'statusUnavailable',
};

/**
 * Maps the platform's HQS-2138 status to what the list shows. Anything the platform sends
 * that HQS-2138 does not define is shown as "status unavailable" rather than guessed at.
 *
 * Two of these are logged at error level, and stay distinguishable in the log even though
 * they present identically: `UNKNOWN` means HQS-2138's own derivation failed, and an
 * unrecognised value means the contract has moved. `FINISHED` is neither -- it is the
 * normal end of a pairing -- so it is not logged.
 */
export function toHealthdotDisplayStatus(value?: string | null): HealthdotDisplayStatus {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();
  const displayStatus = DISPLAY_STATUS[normalized as backend.HealthdotConnectionState];

  if (!displayStatus) {
    void log(
      'healthdotStatus/unmapped',
      new Error(`Connection status "${normalized || '(empty)'}" is not defined by HQS-2138`),
    );
    return 'statusUnavailable';
  }

  if (normalized === 'UNKNOWN') {
    void log(
      'healthdotStatus/unknown',
      new Error('Platform reported connection status UNKNOWN for a Healthdot'),
    );
  }

  return displayStatus;
}
